/**
 * Tools of the map generators (GDD section 10): "maps are generated from a relief profile, then biomes are
 * added". Raw writes into the grid, before the first tick; the generator ends with settle(). Integer-only,
 * from the seeded generator, so identical in every browser.
 */
import { Physique, createRng, nextInt, sinDeg, restLiquids, wakeAll, TRIG_ONE, type Rng, type World } from '@invaders/sim';
import { SEA_LEVEL_FROM_BOTTOM } from '../state';

const { EMPTY, ROCK, WATER, WOOD } = Physique;

export interface Gen {
  world: World;
  rng: Rng;
  w: number;
  h: number;
  /** Row of the sea surface. */
  seaY: number;
  data: Uint8Array;
}

export function gen(world: World, seed: number): Gen {
  const { w, h, data } = world.physique;
  data.fill(EMPTY);
  world.cellState.data.fill(0);
  return { world, rng: createRng(seed), w, h, seaY: h - SEA_LEVEL_FROM_BOTTOM, data };
}

export function inside(g: Gen, x: number, y: number): boolean {
  return x >= 0 && x < g.w && y >= 0 && y < g.h;
}

export function get(g: Gen, x: number, y: number): number {
  return inside(g, x, y) ? g.data[y * g.w + x] : ROCK;
}

export function set(g: Gen, x: number, y: number, mat: number): void {
  if (inside(g, x, y)) g.data[y * g.w + x] = mat;
}

export function rect(g: Gen, x0: number, y0: number, x1: number, y1: number, mat: number, onlyInto?: number): void {
  for (let y = Math.max(0, Math.min(y0, y1)); y <= Math.min(g.h - 1, Math.max(y0, y1)); y++) {
    for (let x = Math.max(0, Math.min(x0, x1)); x <= Math.min(g.w - 1, Math.max(x0, x1)); x++) {
      if (onlyInto === undefined || g.data[y * g.w + x] === onlyInto) g.data[y * g.w + x] = mat;
    }
  }
}

/** Filled ellipse of radii (rx, ry). `onlyInto`: only replaces that material. */
export function ellipse(g: Gen, cx: number, cy: number, rx: number, ry: number, mat: number, onlyInto?: number): void {
  const rx2 = rx * rx;
  const ry2 = ry * ry;
  for (let y = Math.max(0, cy - ry); y <= Math.min(g.h - 1, cy + ry); y++) {
    for (let x = Math.max(0, cx - rx); x <= Math.min(g.w - 1, cx + rx); x++) {
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx * ry2 + dy * dy * rx2 > rx2 * ry2) continue;
      if (onlyInto === undefined || g.data[y * g.w + x] === onlyInto) g.data[y * g.w + x] = mat;
    }
  }
}

/** First row from the top that is not empty in column x (h if none). */
export function surfaceAt(g: Gen, x: number): number {
  for (let y = 0; y < g.h; y++) if (g.data[y * g.w + x] !== EMPTY) return y;
  return g.h;
}

/** A smooth relief from three waves of random phase: `base` ± amplitudes (rows above the sea). */
export function waves(g: Gen, a1: number, f1: number, a2: number, f2: number, a3: number, f3: number): (x: number) => number {
  const p1 = nextInt(g.rng, 360);
  const p2 = nextInt(g.rng, 360);
  const p3 = nextInt(g.rng, 360);
  return (x) =>
    Math.trunc((a1 * sinDeg(Math.trunc((x * 360 * f1) / g.w) + p1)) / TRIG_ONE) +
    Math.trunc((a2 * sinDeg(Math.trunc((x * 360 * f2) / g.w) + p2)) / TRIG_ONE) +
    Math.trunc((a3 * sinDeg(Math.trunc((x * 360 * f3) / g.w) + p3)) / TRIG_ONE);
}

/**
 * An island profile (GDD: the sea borders both ends): `elevation(x)` rows above the sea on land, eased down
 * over `ramp` cells at the coasts, and a sea floor sloping down outside them. Fills rock under the surface;
 * returns the surface row of each column.
 */
export function island(g: Gen, coastL: number, coastR: number, ramp: number, elevation: (x: number) => number): Int32Array {
  const surface = new Int32Array(g.w);
  const r3 = ramp * ramp * ramp;
  for (let x = 0; x < g.w; x++) {
    const c = Math.min(x - coastL, coastR - x);
    let elev: number;
    if (c >= ramp) elev = elevation(x);
    else if (c >= 0) elev = Math.trunc((elevation(x) * c * c * (3 * ramp - 2 * c)) / r3); // smoothstep
    else elev = Math.max(Math.trunc((c * 3) / 4), -(SEA_LEVEL_FROM_BOTTOM - 30));
    surface[x] = g.seaY - elev;
    for (let y = Math.max(0, surface[x]); y < g.h; y++) g.data[y * g.w + x] = ROCK;
  }
  return surface;
}

/** A layer of `mat` on the surface, `depth(x)` cells thick (above the current surface). */
export function layer(g: Gen, surface: Int32Array, x0: number, x1: number, depth: (x: number) => number, mat: number): void {
  for (let x = Math.max(0, x0); x <= Math.min(g.w - 1, x1); x++) {
    const d = depth(x);
    for (let y = surface[x] - d; y < surface[x]; y++) set(g, x, y, mat);
    if (d > 0) surface[x] -= d;
  }
}

/** The sea: every empty cell below the sea surface becomes water. */
export function sea(g: Gen): void {
  for (let y = g.seaY; y < g.h; y++) for (let x = 0; x < g.w; x++) if (g.data[y * g.w + x] === EMPTY) g.data[y * g.w + x] = WATER;
}

/**
 * A tree of wood (GDD: wood burns; a cover or a bridge): a trunk three cells wide from `groundY` up, and a
 * pointed crown of branches, one row of gap between them so that fire and sand get through.
 */
export function tree(g: Gen, x: number, groundY: number, height: number): void {
  const top = groundY - height;
  rect(g, x - 1, top + 2, x + 1, groundY - 1, WOOD);
  const levels = Math.max(2, Math.trunc((height * 2) / 3 / 3));
  for (let k = 0; k < levels; k++) {
    const half = 2 + Math.trunc((k * Math.trunc(height / 3)) / levels);
    rect(g, x - half, top + 3 * k, x + half, top + 3 * k + 1, WOOD);
  }
}

/** Builds `height` of trunk under the ground row too, so that a tree on sand stands on the rock under it. */
export function rootedTree(g: Gen, x: number, rockY: number, groundY: number, height: number): void {
  rect(g, x - 1, groundY, x + 1, rockY, WOOD);
  tree(g, x, groundY, height);
}

/** A tunnel along a wiggly path from (x0, y0) to (x1, y1), `r` cells of radius: dug out (empty). */
export function tunnel(g: Gen, x0: number, y0: number, x1: number, y1: number, r: number, wiggle: number): void {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  let jitter = 0;
  for (let s = 0; s <= n; s += 2) {
    if (s % 16 === 0) jitter = Math.max(-wiggle, Math.min(wiggle, jitter + nextInt(g.rng, 7) - 3));
    const x = x0 + Math.trunc(((x1 - x0) * s) / n);
    const y = y0 + Math.trunc(((y1 - y0) * s) / n) + jitter;
    ellipse(g, x, y, r, r, EMPTY);
  }
}

/** Liquids at rest and every chunk awake: the end of every generator. */
export function settle(g: Gen): void {
  restLiquids(g.world, 0, 0, g.w - 1, g.h - 1);
  wakeAll(g.world);
}

/** Share of each material among the non-empty cells, in percent (GDD maps give these proportions). */
export function shares(world: World): Record<number, number> {
  const counts: Record<number, number> = {};
  let total = 0;
  for (const c of world.physique.data) {
    if (c === EMPTY) continue;
    counts[c] = (counts[c] ?? 0) + 1;
    total++;
  }
  const out: Record<number, number> = {};
  for (const k of Object.keys(counts)) out[Number(k)] = (100 * counts[Number(k)]) / Math.max(1, total);
  return out;
}
